import store from "@/store/index";
import router from "@/router/index";
import { StoreModule } from "@/store/types";
import { GlobalActions } from "@/store/modules/global/types";
import { UserActions } from "@/store/modules/user/types";
import { NotificationService } from "@/services/notification_service";
import { DuplicateCustomerMatch } from "@/models/netsuite/netsuite";

/**
 * Set once a forced logout is under way. A dead token fails every request in
 * flight at once (App.checkAuth alone fires two in parallel), and each 401
 * would otherwise stack another notice and another redirect timer. The page
 * reloads on the way to /login, which resets it.
 */
let loggingOut = false;

export class AuthError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  /**
   * Drops the dead session — token, role, user profile, impersonation — so the
   * SSO round trip starts clean and the persisted store can't hand the stale
   * token back on reload. The cart is left alone: see Header.logout().
   */
  static logout(): void {
    if (loggingOut) return;
    loggingOut = true;
    store.dispatch(StoreModule.Global + GlobalActions.AddLoggedIn, false);
    store.dispatch(StoreModule.Global + GlobalActions.AddRole, null);
    store.dispatch(StoreModule.Global + GlobalActions.AddAuthToken, null);
    store.dispatch(StoreModule.Global + GlobalActions.AddImpersonation, null);
    store.dispatch(StoreModule.User + UserActions.AddUser, {});
    NotificationService.show(
      "Your session has expired. Redirecting to login...",
      "danger"
    );
    setTimeout(() => {
      window.location.replace("/login");
    }, 2000);
  }
}

export class PermissionsError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  static popup(message: string, statusCode: number) {
    NotificationService.show(statusCode + " : " + message, "danger");
  }
}

export class LoginError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  static popup(message: string, statusCode: number) {
    NotificationService.show(statusCode + " : " + message, "danger");
  }
}
export class GenericError extends Error {
  static popup(message: string) {
    NotificationService.show(message, "danger");
  }
}

/**
 * FUPOF-191: create_customer refused to write a second record for a company
 * NetSuite already has. Carries the matching entities so the caller can show
 * them and let the rep either switch the quote to one or say to create anyway.
 */
export class DuplicateCustomerError extends Error {
  constructor(message: string, matches: DuplicateCustomerMatch[]) {
    super(message);
    this.matches = matches;
  }
  public matches: DuplicateCustomerMatch[];
  /**
   * Identifies this error through the layers of catch/rethrow between the
   * service and the review page. `instanceof` is not dependable for a subclass
   * of a builtin once the bundle has been transpiled, and a submit that reports
   * "something went wrong" instead of showing the duplicate is exactly the
   * failure this class exists to prevent.
   */
  public readonly isDuplicateCustomer = true;
}

export function isDuplicateCustomerError(
  err: unknown
): err is DuplicateCustomerError {
  return !!err && (err as any).isDuplicateCustomer === true;
}

export class APIError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  static popup(message: string, statusCode: number) {
    NotificationService.show(statusCode + " : " + message, "danger");
  }
}

export class NotFoundError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  static popup(message: string, statusCode: number) {
    NotificationService.show(statusCode + " : " + message, "danger");
  }
}

export class BadRequest extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
  public statusCode: number;
  static popup(message: string, statusCode: number) {
    NotificationService.show(statusCode + " : " + message, "danger");
  }
}

export class ViewerError extends Error {
  static redirect(err?: string) {
    router.push("/error");
  }
}
